import React, { useState, useEffect, Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { EmpresaProvider, useEmpresa } from "./contexts/EmpresaContext";
import { grupoDeUnidad, nombreDeUnidad } from "./constants/unidades";
import { colors } from "./styles/theme";
import Login from "./pages/Login";
import SeleccionEmpresa from "./pages/SeleccionEmpresa";
import AccesoRapidoAuditorias from "./pages/AccesoRapidoAuditorias";
import LayoutFoco from "./components/LayoutFoco";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import ErrorReporter from "./components/ErrorReporter";

// Cada módulo se descarga solo hasta que alguien lo abre, en vez de todo
// junto al entrar — así la carga inicial es más rápida, sobre todo en
// celular. Login/selector/Layout se quedan fuera de esto porque siempre
// se necesitan de entrada.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Ventas = lazy(() => import("./pages/Ventas"));
const Gastos = lazy(() => import("./pages/Gastos"));
const Reportes = lazy(() => import("./pages/Reportes"));
const CajaChica = lazy(() => import("./pages/CajaChica"));
const Tareas = lazy(() => import("./pages/Tareas"));
const Empleados = lazy(() => import("./pages/Empleados"));
const Propinas = lazy(() => import("./pages/Propinas"));
const Bancos = lazy(() => import("./pages/Bancos"));
const Inventario = lazy(() => import("./pages/Inventario"));
const Nomina = lazy(() => import("./pages/Nomina"));
const Asistencia = lazy(() => import("./pages/Asistencia"));
const CorteCaja = lazy(() => import("./pages/CorteCaja"));
const DashboardGerente = lazy(() => import("./pages/DashboardGerente"));
const DepositoCaja = lazy(() => import("./pages/DepositoCaja"));
const Presupuesto = lazy(() => import("./pages/Presupuesto"));
const ReporteSemanal = lazy(() => import("./pages/ReporteSemanal"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const ChecklistOperativo = lazy(() => import("./pages/ChecklistOperativo"));
const AuditoriaHigiene = lazy(() => import("./pages/AuditoriaHigiene"));
const Historial = lazy(() => import("./pages/Historial"));

const cargando = (
  <div style={{ padding: "60px", textAlign: "center", color: colors.inkSoft, fontSize: "13px" }}>Cargando...</div>
);

// Roles "operativos": no eligen empresa al entrar, van directo a su panel.
const ROLES_OPERATIVOS = ["gerente", "gerente_calidad", "supervisor_tiendas"];
// De estos, solo gerente y gerente_calidad están fijos a UNA unidad para sus
// módulos normales (Dashboard, Ventas, etc). supervisor_tiendas puede tener
// varias, y gerente_calidad elige la unidad dentro de la propia Auditoría.
const ROLES_UNIDAD_UNICA = ["gerente", "gerente_calidad"];
const PAGINA_INICIAL_POR_ROL = { gerente: "caja_chica", gerente_calidad: "dashboard", supervisor_tiendas: "caja_chica" };
// Bryan y Yazmin entran directo a Auditorías desde una pantalla ligera, sin
// el menú completo — pensada para usarse parados en la tienda, en celular.
const ROLES_ACCESO_RAPIDO = ["gerente_calidad", "supervisor_tiendas"];

function AppContent() {
  const { currentUser, unidadAsignada, unidadesAsignadas, rol, userProfile, logout } = useAuth();
  const { empresa, setEmpresa } = useEmpresa();
  const esRolOperativo = ROLES_OPERATIVOS.includes(rol);
  const esRolUnidadUnica = ROLES_UNIDAD_UNICA.includes(rol);
  const esRolAccesoRapido = ROLES_ACCESO_RAPIDO.includes(rol);
  const [page, setPage] = useState(PAGINA_INICIAL_POR_ROL[rol] || "dashboard");
  const [empresaElegida, setEmpresaElegida] = useState(false);
  const [modoSistemaCompleto, setModoSistemaCompleto] = useState(false);
  const [tiendaAuditoria, setTiendaAuditoria] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setEmpresaElegida(false); setModoSistemaCompleto(false);
      setTiendaAuditoria(null);
      return;
    }
    // Los roles operativos no eligen empresa — su unidad ya la determina
    if (esRolOperativo) {
      const grupoAsignado = grupoDeUnidad(unidadAsignada || unidadesAsignadas[0]);
      if (grupoAsignado && grupoAsignado !== empresa) setEmpresa(grupoAsignado);
      setEmpresaElegida(true);
    }
  }, [currentUser, rol, unidadAsignada]);

  if (!currentUser) return <Login />;

  if (!esRolOperativo && !empresaElegida) {
    return (
      <SeleccionEmpresa
        nombreUsuario={userProfile?.nombre}
        onSeleccionar={(grupoId) => { setEmpresa(grupoId); setEmpresaElegida(true); }}
        onLogout={logout}
      />
    );
  }

  if (esRolAccesoRapido && !modoSistemaCompleto) {
    if (tiendaAuditoria) {
      return (
        <LayoutFoco titulo={`Auditoría — ${nombreDeUnidad(tiendaAuditoria)}`} onSalir={() => setTiendaAuditoria(null)}>
          <Suspense fallback={cargando}>
            <AuditoriaHigiene unidadFija={tiendaAuditoria} />
          </Suspense>
        </LayoutFoco>
      );
    }
    return (
      <AccesoRapidoAuditorias
        nombreUsuario={userProfile?.nombre}
        onElegirTienda={setTiendaAuditoria}
        onEntrarSistema={() => setModoSistemaCompleto(true)}
        onLogout={logout}
      />
    );
  }

  // Gerente de Tienda y Gerente de Higiene y Calidad quedan fijos a su unidad
  const unidadFija = esRolUnidadUnica ? unidadAsignada : null;

  const pages = {
    dashboard: esRolOperativo ? <DashboardGerente unidad={unidadAsignada || unidadesAsignadas[0]} isAdmin={false} /> : <Dashboard unidadFija={unidadFija} />,
    ventas: <Ventas unidadFija={unidadFija} onIrAReportes={(tabId, unidadId) => {
      try {
        localStorage.setItem("reportesTabInicial", tabId);
        if (unidadId) localStorage.setItem("reportesUnidadInicial", unidadId);
      } catch (e) {}
      setPage("reportes");
    }} />,
    gastos: <Gastos />,
    caja_chica: <CajaChica unidadFija={unidadFija} />,
    inventario: <Inventario unidadFija={unidadFija} />,
    empleados: <Empleados />,
    tareas: <Tareas />,
    reportes: <Reportes />,
    usuarios: <Usuarios />,
    propinas: <Propinas unidadFija={unidadFija} />,
    bancos: <Bancos />,
    nomina: <Nomina unidadFija={unidadFija} />,
    asistencia: <Asistencia unidadFija={unidadFija} />,
    corte_caja: <CorteCaja unidadFija={unidadFija} />,
    deposito_caja: <DepositoCaja />,
    presupuesto: esRolOperativo ? <Presupuesto unidadFija={unidadFija} soloLectura={true} /> : <Presupuesto />,
    reporte_semanal: <ReporteSemanal />,
    checklist: <ChecklistOperativo unidadFija={unidadFija} />,
    auditoria: <AuditoriaHigiene />,
    historial: <Historial />
  };

  return (
    <Layout currentPage={page} onNavigate={setPage} onCambiarEmpresa={() => setEmpresaElegida(false)}>
      <Suspense fallback={cargando}>
        {pages[page] || <Dashboard />}
      </Suspense>
    </Layout>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <EmpresaProvider>
          <ErrorReporter />
          <AppContent />
        </EmpresaProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
